import { useMemo } from 'react';

import {
	Address,
	AnnouncementOutlineIcon,
	Book,
	Calendar,
	Countries,
	CurrenciesIcon,
	Customers,
	DashboardIcon,
	EmailIcon,
	Faq,
	FileManagerIcon,
	Location,
	LocationApproval,
	Merchandisers,
	OrderSearchIcon,
	Plant,
	Products,
	Programs,
	ProjectsIcon,
	ReturnsIcon,
	ShoppingBagOutline,
	SubPrograms,
	Success,
	SupportIcon,
	Upload,
	Users,
} from '@apple/assets/icons';
import { useTranslation } from '@apple/lib/i18next';
import type { MenuItem, MenuItemGroup } from '@apple/ui/layouts/components/menu-item';

import { useAuthContext } from '@apple/features/auth-channel/hooks/useAuthContext';

export function useAppleChannelNavMenu() {
	const { t } = useTranslation('common');
	const { filterByPermissions } = useAuthContext();

	const menuItems = useMemo(() => {
		return filterByPermissions<MenuItemGroup>([
			{
				requireAuth: true,
				items: filterByPermissions<MenuItem>([
					// FIXME: Merge the two dashboard routes into one
					{
						label: t('mainMenu.dashboard'),
						icon: DashboardIcon,
						to: '/',
						requireAuth: true,
					},
					{
						label: t('mainMenu.shop'),
						icon: ShoppingBagOutline,
						to: '/cart',
						requireAuth: true,
					},
					{
						label: t('mainMenu.orders'),
						icon: OrderSearchIcon,
						to: '/orders/search',
						requireAuth: true,
					},
					{
						label: t('mainMenu.returns'),
						icon: ReturnsIcon,
						to: '/returns',
						requireAuth: true,
						requirePermission: 'AppleBrandedPrograms.Security.Features.Returns',
					},
					{
						label: t('shell.addressBook'),
						icon: Book,
						to: '/address-book',
						requireAuth: true,
					},
					{
						label: t('mainMenu.help'),
						icon: SupportIcon,
						to: '/help',
						requireAuth: true,
					},
				]),
			},
			{
				label: t('mainMenu.approvals.label'),
				requireAuth: true,
				items: filterByPermissions<MenuItem>([
					{
						icon: Success,
						label: t('mainMenu.approvals.orderApprovals'),
						to: '/approvals/orders',
						requirePermission: 'Manzanita.Security.Features.OrderManagement.Read',
					},
					{
						icon: LocationApproval,
						label: t('mainMenu.approvals.locationApprovals'),
						to: '/approvals/location',
						requirePermission:
							'AppleBrandedPrograms.Security.Features.LocationApprovalManagement',
					},
				]),
			},
			{
				label: t('mainMenu.distroOrders.label'),
				requirePermission: 'Manzanita.Security.Orders.CanPlaceDistroOrders',
				items: filterByPermissions<MenuItem>([
					{
						icon: Upload,
						label: t('mainMenu.distroOrders.upload'),
						to: '/distro/upload',
						requirePermission: 'Manzanita.Security.Orders.CanPlaceDistroOrders',
					},
					{
						icon: Calendar,
						label: t('mainMenu.distroOrders.history'),
						to: '/distro/history',
						requirePermission: 'Manzanita.Security.Features.DistroOrderHistory',
					},
				]),
			},
			{
				label: t('mainMenu.manage.label'),
				requirePermission: 'Manzanita.Security.Features.Snapshot',
				items: filterByPermissions<MenuItem>([
					{
						icon: Address,
						label: t('mainMenu.manage.addresses'),
						to: '/manage/addresses',
						requirePermission:
							'AppleBranded.Security.ShipToAddress.CanEditOtherUsersAddresses',
					},
					// {
					// 	icon: Countries,
					// 	label: t('mainMenu.manage.countries'),
					// 	to: '/manage/countries',
					// 	requirePermission: 'Manzanita.Security.Features.CountryManagement.Read',
					// 	disabled: true,
					// },
					{
						icon: CurrenciesIcon,
						label: t('mainMenu.manage.currencies'),
						to: '/manage/currencies',
						requirePermission: 'Manzanita.Security.Features.CustomerManagement',
					},
					// {
					// 	icon: LineOfBusiness,
					// 	label: t('mainMenu.manage.productLines'),
					// 	to: '/manage/line-of-business',
					// 	requirePermission:
					// 		'Manzanita.Security.Features.ProductCategoryManagement.Read',
					// 	disabled: true,
					// },
					// {
					// 	icon: Location,
					// 	label: t('mainMenu.manage.locations'),
					// 	to: '/manage/locations',
					// 	requirePermission:
					// 		'AppleBrandedPrograms.Security.Features.AppleIdFeedLocationsManagement.Read',
					// 	disabled: true,
					// },
					{
						icon: Plant,
						label: t('mainMenu.manage.plants'),
						to: '/manage/plants',
						requirePermission: 'Manzanita.Security.Features.PlantManagement.Read',
					},
					{
						icon: Products,
						label: t('mainMenu.manage.products'),
						to: '/manage/products',
						requirePermission: 'Manzanita.Security.Features.ProductManagement.Read',
					},
					{
						icon: Users,
						label: t('mainMenu.manage.users'),
						to: '/manage/users',
						requirePermission: 'Manzanita.Security.Features.UserManagement.Read',
					},
					{
						icon: Merchandisers,
						label: t('mainMenu.manage.merchandisers'),
						to: '/manage/merchandisers',
						requirePermission:
							'AppleBrandedPrograms.Security.Features.AppleMerchandisersManagement.Read',
					},
					{
						icon: Countries,
						label: t('mainMenu.manage.countries'),
						to: '/manage/countries',
						requirePermission: 'Manzanita.Security.Features.CountryManagement.Read',
					},
					{
						icon: Customers,
						label: t('mainMenu.manage.customers'),
						to: '/manage/customers',
						requirePermission: 'Manzanita.Security.Features.CustomerManagement',
					},
					{
						icon: Programs,
						label: t('mainMenu.manage.programs'),
						to: '/manage/programs',
						requirePermission:
							'Manzanita.Security.Features.CustomerGroupManagement.Read',
					},
					{
						icon: SubPrograms,
						label: t('mainMenu.manage.subPrograms'),
						to: '/manage/subprograms',
						requirePermission:
							'Manzanita.Security.Features.CustomerGroupCountryManagement.Read',
					},
				]),
			},
			{
				label: t('mainMenu.content.label'),
				requirePermission: 'Manzanita.Security.Features.ContentManagement',
				items: filterByPermissions<MenuItem>([
					{
						icon: AnnouncementOutlineIcon,
						label: t('mainMenu.content.announcements'),
						to: '/content/announcements',
					},
					{
						icon: Faq,
						label: t('mainMenu.content.faqs'),
						to: '/content/faq',
					},
					{
						icon: ProjectsIcon,
						label: t('mainMenu.content.projects'),
						to: '/content/projects',
					},
					{
						icon: FileManagerIcon,
						label: t('mainMenu.content.fileManager'),
						to: '/content/file-manager',
						requirePermission: 'AppleBranded.Security.UserFileRepository.View',
					},
				]),
			},
			{
				label: t('mainMenu.reports.label'),
				requirePermission: 'Manzanita.Security.Features.Reports',
				items: filterByPermissions<MenuItem>([
					{
						icon: EmailIcon,
						label: t('mainMenu.reports.emails'),
						to: '/reports/emails',
						requirePermission: 'Manzanita.Security.Features.Reports',
					},
					// {
					// 	icon: Products,
					// 	label: t('mainMenu.manage.products'),
					// 	to: '/reports/products',
					// 	requirePermission: 'Manzanita.Security.Features.Reports',
					// 	disabled: true,
					// },
					// {
					// 	icon: Users,
					// 	label: t('mainMenu.manage.users'),
					// 	to: '/reports/users',
					// 	requirePermission: 'Manzanita.Security.Features.Reports',
					// 	disabled: true,
					// },
					{
						icon: SupportIcon,
						label: t('mainMenu.reports.supportRequests'),
						to: '/reports/supportRequests',
						requirePermission: 'Manzanita.Security.Features.Reports',
					},
					{
						icon: Location,
						label: t('mainMenu.reports.locationFeed'),
						to: '/reports/location-feed',
						requirePermission:
							'AppleBrandedPrograms.Security.Features.AppleIdFeedLocationsManagement.Read',
					},
				]),
			},
		]);
	}, [filterByPermissions, t]);

	return { menuItems };
}
